/**
 * The clip list — the phone's editing surface.
 *
 * One card per clip: a poster cut from the user's own video, the time it covers, and a circle
 * you tap to keep it. Kept clips flow into the timeline dock at the bottom, where they can be
 * re-ordered, trimmed and turned into one video to share.
 *
 * Rules carried over from the web ET: playback keys by CLIP ID (`v-${id}`), never a row index,
 * and posters live in a per-id map, never inside the rows (writing them into rows would re-run
 * the whole filter pipeline).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BackButton } from "./MobileApp";
import { CherryMark, CherryTick } from "./Brand";
import MobileTimeline from "./MobileTimeline";
import { M } from "./mobileStrings";
import { CHOP_SECONDS, MODES, fmtClock, fmtSpan, picksInOrder, searchRows, trimDelta, fmtDelta, wordAt,
} from "./mobileModel";
import { createPosterExtractor } from "./mediaProbe";
import { canExport, canShare, exportPicks, shareVideo } from "./nativeMedia";
import { buzz, pick } from "./native";

const EPS = 0.03; // stop a hair before the cut so a clip never bleeds into the next one

/** Drive one <video> through [start, end) with a rAF loop; timeupdate is too coarse for 2s clips. */
function useRangePlayback(videoRef, range, active, onDone, onFail, onTime) {
  const cb = useRef({ onDone, onFail, onTime });
  cb.current = { onDone, onFail, onTime };
  useEffect(() => {
    if (!active || !range) return undefined;
    const v = videoRef.current;
    if (!v) return undefined;
    let raf = 0, cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (cb.current.onTime) cb.current.onTime(v.currentTime);
      if (v.currentTime >= range.end - EPS || v.ended) { v.pause(); cb.current.onDone(); return; }
      raf = requestAnimationFrame(tick);
    };
    const start = () => {
      if (cancelled) return;
      try { v.currentTime = range.start; } catch (e) { /* not seekable yet */ }
      const p = v.play();
      // A rejected play() is how a WebView refuses playback. Say so instead of looking frozen.
      if (p && p.catch) p.catch(() => { if (!cancelled) cb.current.onFail(); });
      raf = requestAnimationFrame(tick);
    };
    if (v.readyState >= 1) start(); else v.addEventListener("loadedmetadata", start, { once: true });
    const onErr = () => { if (!cancelled) cb.current.onFail(); };
    v.addEventListener("error", onErr);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      v.removeEventListener("loadedmetadata", start);
      v.removeEventListener("error", onErr);
    };
  }, [videoRef, range, active]);
}

function ClipCard({ row, thumb, order, active, showBadge, stitched, trim, onPlay, onEnded, onFail, onToggle, onResetTrim }) {
  const vref = useRef(null);
  const [ready, setReady] = useState(false);
  // A trimmed clip plays its trimmed range — what you will actually get.
  const delta = useMemo(() => trimDelta(row, trim), [row, trim]);
  const range = useMemo(
    () => (delta ? { start: delta.start, end: delta.end } : { start: row.start_in, end: row.end_in }),
    [row, delta],
  );
  // Synchronised transcript: the word being spoken lights up. Only re-render when the word
  // changes, not every frame.
  const [wordIdx, setWordIdx] = useState(-1);
  const onTime = useCallback((t) => {
    const i = wordAt(row.utterance, t);
    setWordIdx((cur) => (cur === i ? cur : i));
  }, [row.utterance]);
  useRangePlayback(vref, range, active, onEnded, onFail, onTime);
  useEffect(() => { if (!active) { setReady(false); setWordIdx(-1); } }, [active]);
  const words = useMemo(() => (row.sentence ? row.sentence.split(/\s+/).filter(Boolean) : []), [row.sentence]);
  const canLight = active && Array.isArray(row.utterance) && row.utterance.length > 0 && words.length === row.utterance.length;
  const a = stitched ? row.tl_start : row.start_in;
  const b = stitched ? row.tl_end : row.end_in;
  const audio = !!(row.source && row.source.audioOnly);
  return (
    <div className={`ccm-clip${active ? " is-playing" : ""}${order ? " is-picked" : ""}${row._gap ? " is-gap" : ""}`}>
      {showBadge ? <span className="stripe" style={{ background: row.source.color }} /> : null}
      <div className={`ccm-poster${audio ? " is-audio" : ""}`} onClick={onPlay} role="button" aria-label={fmtSpan(a, b)}>
        {/* The poster stays underneath while the video opens, so tapping never flashes black
            or leaves a dead control on screen — the picture simply starts moving. */}
        {thumb && !audio ? <img src={thumb} alt="" draggable={false} /> : <span className="ph" />}
        {audio ? (
          // No picture exists for this one, so do not put a video frame on screen: an <audio>
          // element plays the sound and the tile says what it is.
          <span className="snd" aria-hidden="true"><CherryMark size={26} color="currentColor" />{M.audioOnly}</span>
        ) : null}
        {active ? (
          audio ? (
            <audio id={`v-${row.id}`} ref={vref} src={row.source.video} preload="auto" onPlaying={() => setReady(true)} />
          ) : (
            <video
              id={`v-${row.id}`} ref={vref} src={row.source.video} playsInline preload="auto"
              className={ready ? "is-ready" : ""}
              onPlaying={() => setReady(true)}
            />
          )
        ) : null}
        {active && !ready && !audio ? (
          <span className="load" aria-hidden="true"><CherryMark size={22} color="#fff" /></span>
        ) : null}
        {!active && thumb && !audio ? <span className="play" aria-hidden="true" /> : null}
      </div>
      <div className="meta" onClick={onPlay}>
        {row._gap
          ? <span className="said said--gap">〰 {M.silence}</span>
          : row.sentence
            ? (canLight ? (
              <span className="said said--live">
                {words.map((w, i) => (
                  <span key={i} className={i === wordIdx ? "w is-on" : (i < wordIdx ? "w was" : "w")}>{w}{i < words.length - 1 ? " " : ""}</span>
                ))}
              </span>
            ) : <span className="said">{row.sentence}</span>)
            : <span className="tc">{fmtSpan(a, b)}</span>}
        <span className="sub">
          {row.speaker ? <span className="who">{row.speaker}</span> : null}
          {delta ? (
            // The list shows what the clip IS now, not what it was cut as: the trimmed span in
            // cherry, the change at each end, and a way to put it back.
            <>
              <span className="tc-sm is-trimmed">{fmtSpan(delta.start, delta.end)}</span>
              <span className="delta">{[fmtDelta(delta.dStart), fmtDelta(delta.dEnd)].filter(Boolean).join(" ")}</span>
              <button
                type="button" className="ccm-untrim" aria-label={M.untrim} title={M.untrim}
                onClick={(e) => { e.stopPropagation(); onResetTrim(); }}
              >↺</button>
            </>
          ) : (row.sentence || row._gap ? <span className="tc-sm">{fmtSpan(a, b)}</span> : null)}
          {showBadge ? <span className="ccm-badge" style={{ background: row.source.color }}>{row.source.short}</span> : null}
          <span>{delta ? (delta.end - delta.start).toFixed(1) : row.sec_float}s</span>
          {active ? <span className="now">▶</span> : null}
        </span>
      </div>
      <button
        type="button"
        className="ccm-keep"
        onClick={onToggle}
        aria-pressed={!!order}
        aria-label={order ? `Kept as clip ${order}` : "Keep this clip"}
      >
        <CherryTick on={!!order} order={order} />
      </button>
    </div>
  );
}

export default function MobileClips({
  project, selected, trims, onToggle, onRemove, onTrim, onResetTrim, onReorder, onBack, onSettings, onReCut,
}) {
  const { rows, sources, mode, chopSeconds } = project;
  const [thumbs, setThumbs] = useState({});    // clip id -> data: URL, never inside the rows
  const [activeId, setActiveId] = useState(null);
  const [hidden, setHidden] = useState(() => new Set());
  const [playing, setPlaying] = useState(false);
  const [nowIndex, setNowIndex] = useState(0);
  const [sharing, setSharing] = useState(false);
  const [shareMsg, setShareMsg] = useState("");
  const [notice, setNotice] = useState("");
  const [query, setQuery] = useState("");
  const [showGaps, setShowGaps] = useState(false);
  const [speaker, setSpeaker] = useState("");   // "" = everyone
  const [mood, setMood] = useState("");         // "" = any
  const listRef = useRef(null);
  const exRef = useRef(null);

  // Posters: the server's own when a row has one (a library video — its file is not on the
  // phone, and a canvas cannot read frames from a cross-origin stream anyway), otherwise made
  // on the phone from the user's video, one at a time.
  useEffect(() => {
    const served = {};
    rows.forEach((r) => { if (r.thumbnail) served[r.id] = r.thumbnail; });
    setThumbs(served);
    const ex = createPosterExtractor({ width: 220 });
    exRef.current = ex;
    let alive = true;
    const batch = {};
    let raf = 0;
    const flush = () => {
      raf = 0;
      if (!alive) return;
      const add = { ...batch };
      Object.keys(batch).forEach((k) => delete batch[k]);
      setThumbs((prev) => ({ ...prev, ...add }));
    };
    rows.forEach((r) => {
      if (r.thumbnail || (r.source && r.source.remote)) return;   // nothing to extract, or cannot
      ex.enqueue(r.id, r.source.video, r.start_in).then((url) => {
        if (!alive || !url) return;
        batch[r.id] = url;
        if (!raf) raf = requestAnimationFrame(flush);
      });
    });
    return () => { alive = false; if (raf) cancelAnimationFrame(raf); ex.destroy(); exRef.current = null; };
  }, [rows]);

  // Whatever is on screen gets its picture first, so scrolling never waits on the whole list.
  const onScroll = useCallback(() => {
    const el = listRef.current, ex = exRef.current;
    if (!el || !ex) return;
    const top = el.scrollTop, bottom = top + el.clientHeight;
    const visible = [];
    el.querySelectorAll("[data-clip]").forEach((node) => {
      if (node.offsetTop + node.offsetHeight >= top && node.offsetTop <= bottom) {
        visible.push(node.getAttribute("data-clip"));
      }
    });
    if (visible.length) ex.prioritize(visible);
  }, []);

  /**
   * The website's focus button, ported: after a search or a filter, bring this clip back into
   * its surroundings. Clears the search, the speaker and mood filters, shows the gaps (as the
   * website shows spaces), keeps every pick, then scrolls to the clip and flashes it.
   */
  const [revealed, setRevealed] = useState(null);
  const revealInContext = useCallback((id) => {
    setQuery(""); setSpeaker(""); setMood(""); setShowGaps(true);
    setRevealed(id);
    buzz(8);
    // Two frames: one for the filters to clear, one for the fuller list to lay out.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = listRef.current;
      const node = el && el.querySelector(`[data-clip="${String(id).replace(/"/g, '\\"')}"]`);
      if (node && node.scrollIntoView) node.scrollIntoView({ block: "center", behavior: "smooth" });
    }));
    window.setTimeout(() => setRevealed((cur) => (cur === id ? null : cur)), 1600);
  }, []);
  const narrowed = !!(query || speaker || mood);

  const showBadge = mode === MODES.SEPARATE && sources.length > 1;
  const gapCount = useMemo(() => rows.filter((r) => r._gap).length, [rows]);
  // These only exist after the cloud pass — the on-device recogniser knows neither.
  const speakers = useMemo(
    () => [...new Set(rows.map((r) => r.speaker).filter(Boolean))].sort(),
    [rows],
  );
  const moods = useMemo(
    () => [...new Set(rows.filter((r) => !r._gap && r.sentence).map((r) => r.sentiment))]
      .filter((m) => m && m !== "neutral").length
      ? ["positive", "neutral", "negative"] : [],
    [rows],
  );
  const visibleRows = useMemo(() => {
    let out = hidden.size ? rows.filter((r) => !hidden.has(String(r.source.transcriptId))) : rows;
    if (!showGaps) out = out.filter((r) => !r._gap);   // the quiet parts, until asked for
    if (speaker) out = out.filter((r) => r.speaker === speaker);
    if (mood) out = out.filter((r) => r.sentiment === mood);
    return project.spoken ? searchRows(out, query) : out;
  }, [rows, hidden, project.spoken, query, showGaps, speaker, mood]);
  const orderOf = useMemo(() => {
    const m = new Map();
    selected.forEach((id, i) => m.set(String(id), i + 1));
    return m;
  }, [selected]);
  const picks = useMemo(() => picksInOrder(rows, selected, thumbs, trims), [rows, selected, thumbs, trims]);
  const haveThumbs = Object.keys(thumbs).length;

  const toggleSource = (s) => setHidden((h) => {
    const next = new Set(h);
    const k = String(s.transcriptId);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  /**
   * Keep every clip the search found, in the order they were said — a supercut of a word in one
   * tap. This is the whole promise of searching your own footage: find "candles", keep them all,
   * share. Anything already kept is left alone so the order you built is not disturbed.
   */
  const keepAllFound = () => {
    const already = new Set(selected.map(String));
    visibleRows.filter((r) => !already.has(String(r.id))).forEach((r) => onToggle(r.id));
    pick();
  };

  const share = async () => {
    if (!picks.length) return;
    // A library video streams from CherryClipz; there is no file here for the on-device
    // exporter to cut. The server can (that is where captions live) — next step, say so.
    if ((project.sources || []).some((s) => s.remote)) { setShareMsg(M.libraryStreamNote); return; }
    if (!canExport() || !canShare()) { setShareMsg(M.shareNotHere); return; }
    setSharing(true); setShareMsg(""); setPlaying(false); setActiveId(null);
    try {
      const out = await exportPicks(picks, project.paths, (f) => setShareMsg(M.makingPct(Math.round(f * 100))));
      setShareMsg("");
      await shareVideo(out.path, "");
    } catch (e) {
      setShareMsg(e && e.message === "no-exporter" ? M.shareNotHere : M.shareFailed);
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="ccm-screen ccm-screen--clips">
      <div className="ccm-top">
        <BackButton onClick={onBack} />
        <h1>{project.spoken ? M.spokenTitle(rows.length) : M.clipsTitle(chopSeconds, rows.length)}</h1>
        <button type="button" className="ccm-info" aria-label={M.whatsInHere} onClick={onSettings}>?</button>
      </div>

      {showBadge ? (
        <div className="ccm-chips">
          {sources.map((s) => (
            <button
              type="button"
              key={s.transcriptId}
              className={`ccm-chip${hidden.has(String(s.transcriptId)) ? " is-off" : ""}`}
              onClick={() => toggleSource(s)}
              aria-pressed={!hidden.has(String(s.transcriptId))}
            >
              <span className="dot" style={{ background: s.color }} />
              <span className="lbl">{s.short}</span>
              <span className="dur">{fmtClock(s.duration)}</span>
            </button>
          ))}
        </div>
      ) : null}

      {project.spoken ? (
        <div className="ccm-search">
          <span aria-hidden="true">🔍</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={M.searchPlaceholder}
            aria-label={M.searchPlaceholder}
          />
          {query ? (
            <button type="button" onClick={() => setQuery("")} aria-label={M.clearSearch}>✕</button>
          ) : null}
        </div>
      ) : null}

      {project.spoken && query && visibleRows.length ? (
        <button type="button" className="ccm-keepall" onClick={keepAllFound}>
          🍒 {M.keepAll(visibleRows.length)}
        </button>
      ) : null}

      {speakers.length > 1 || moods.length ? (
        <div className="ccm-chips ccm-chips--filters">
          {speakers.length > 1 ? (
            <>
              <button type="button" className={`ccm-chip${speaker ? "" : " is-picked"}`}
                      onClick={() => { buzz(); setSpeaker(""); }}>{M.everyone}</button>
              {speakers.map((sp) => (
                <button type="button" key={sp} className={`ccm-chip${speaker === sp ? " is-picked" : ""}`}
                        onClick={() => { buzz(); setSpeaker(speaker === sp ? "" : sp); }}>
                  {sp}
                </button>
              ))}
            </>
          ) : null}
          {moods.length ? (
            <>
              <button type="button" className={`ccm-chip${mood ? "" : " is-picked"}`}
                      onClick={() => { buzz(); setMood(""); }}>{M.allMoods}</button>
              {moods.map((m) => (
                <button type="button" key={m} className={`ccm-chip ccm-chip--${m}${mood === m ? " is-picked" : ""}`}
                        onClick={() => { buzz(); setMood(mood === m ? "" : m); }}>
                  {M.moods[m] || m}
                </button>
              ))}
            </>
          ) : null}
        </div>
      ) : null}

      {/* the quiet between sentences: a breath is often the clip you want */}
      {gapCount ? (
        <button
          type="button"
          className={`ccm-gaps${showGaps ? " is-on" : ""}`}
          onClick={() => { buzz(); setShowGaps((v) => !v); }}
          aria-pressed={showGaps}
        >
          {showGaps ? M.hideSilences : M.showSilences} · {gapCount}
        </button>
      ) : null}

      {/* change your mind about the size, without starting over */}
      {!project.spoken && onReCut ? (
        <div className="ccm-recut">
          <span className="lbl">{M.reCut}</span>
          {CHOP_SECONDS.map((n) => (
            <button
              type="button"
              key={n}
              className={`ccm-recut-btn${n === chopSeconds ? " is-on" : ""}`}
              onClick={() => { buzz(); if (n !== chopSeconds) onReCut(n); }}
            >
              {M.seconds(n)}
            </button>
          ))}
        </div>
      ) : null}

      {haveThumbs < rows.length ? (
        <div className="ccm-progress" aria-hidden="true">
          <span style={{ width: `${Math.round((haveThumbs / Math.max(1, rows.length)) * 100)}%` }} />
        </div>
      ) : null}
      {notice ? <div className="ccm-error ccm-error--slim" role="alert">{notice}</div> : null}

      {/* A library video streams; its index and first bytes are fetched once here so that the
          first tap on a clip is a seek, not a cold start. (The real fix is storing uploads with
          the index at the front — worker-side; this is the phone's half.) */}
      {(project.sources || []).filter((s) => s.remote && !s.audioOnly && s.video).map((s) => (
        <video key={`warm-${s.id}`} className="ccm-warm" src={s.video} preload="auto" muted playsInline aria-hidden="true" />
      ))}
      <div className="ccm-clips" ref={listRef} onScroll={onScroll}>
        {query && !visibleRows.length ? <p className="ccm-hint ccm-empty">{M.noMatches(query)}</p> : null}
        {visibleRows.map((r) => (
          <div data-clip={r.id} key={r.id} className={revealed === r.id ? "is-revealed" : undefined}>
            {narrowed ? (
              <button
                type="button"
                className="ccm-reveal"
                onClick={(e) => { e.stopPropagation(); revealInContext(r.id); }}
                aria-label={M.revealInContext}
                title={M.revealInContext}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <circle cx="12" cy="12" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
                  <circle cx="12" cy="12" r="1.8" fill="currentColor" />
                  <path d="M12 2.5v4M12 17.5v4M2.5 12h4M17.5 12h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            ) : null}
            <ClipCard
              row={r}
              thumb={thumbs[r.id]}
              order={orderOf.get(String(r.id))}
              active={activeId === r.id}
              showBadge={showBadge}
              stitched={mode === MODES.STITCH}
              trim={trims ? trims[r.id] : null}
              onResetTrim={() => onResetTrim(r.id)}
              onPlay={() => { setPlaying(false); setNotice(""); setActiveId((cur) => (cur === r.id ? null : r.id)); }}
              onEnded={() => setActiveId((cur) => (cur === r.id ? null : cur))}
              onFail={() => { setActiveId(null); setNotice(M.playFailed); }}
              onToggle={() => { pick(); onToggle(r.id); }}
            />
          </div>
        ))}
      </div>

      <MobileTimeline
        picks={picks}
        rows={rows}
        playing={playing}
        nowIndex={nowIndex}
        onPlay={() => { setActiveId(null); setNotice(""); setPlaying(true); }}
        onStop={() => setPlaying(false)}
        onNowIndex={setNowIndex}
        onTrim={onTrim}
        onRemove={onRemove}
        onReorder={onReorder}
        onShare={share}
        sharing={sharing}
        shareError={shareMsg}
      />
    </div>
  );
}
